import { call, select, all } from 'redux-saga/effects';
import { Texture } from 'three';
import {
    KintekiArrowModel,
    EntekiArrowModel,
    MakiwaraArrowModel,
} from '@app/react/3d/ArrowModelInterface';
import { Apply, Apply2, ApplyMultiple } from '@app/react/3d/texHandler';
import type { MAKIWARA_THREAD_MODE, PartsProduct, PartsProductOptions } from '@app/react/const/type';
import type { AppState } from '@app/react/reducer';
import { range } from '@app/react/common/range';
import { isFeatherNone } from '@app/react/common/isFeatherNone';
import { texLoader } from '@app/react/common/texLoader';
import { safe } from '@app/react/saga/common/safe';

type TextureRT = { result: Texture | null, error: Error | null };

const fetchTexture = (path: string): Promise<Texture> => (
    new Promise((resolve) => {
        texLoader.load(path, resolve);
    })
);

export interface TextureData {
    name: string;
    textureImages: Array<string>;
    options: PartsProductOptions;
}

export interface FamilycrestColorTextureData {
    name: string;
    textureImage: string;
}

export type GetPartsHandler = (state: AppState) => TextureData | null;

export type GetPartsHandler2 = (
    state: AppState
) => FamilycrestColorTextureData | null;

export type GetModelHandler = (
    state: AppState
) => KintekiArrowModel | EntekiArrowModel | MakiwaraArrowModel | null;

export const fetchAndApply = (
    apply: Apply,
    getParts: GetPartsHandler,
    partsName: string,
    reset: () => void,
) => function* fetchAndApplySaga(): Generator<any, any, any> {
    const parts: TextureData | null = yield select<GetPartsHandler>(
        getParts,
    );
    if (parts === null) {
        console.log(`[saga] reset material for ${partsName}`);
        reset();
        return;
    }
    if (!(parts.textureImages.length >= 1)) {
        console.error(
            '[saga] load texture error: not found parts or texImg=',
            parts,
        );
        reset();
        return;
    }

    const { result: tex, error }: TextureRT = yield safe(
        call(fetchTexture, parts.textureImages[0]),
    );
    if (error !== null) {
        console.error('[saga] load texture error:', error);
        return;
    }
    if (tex === null) {
        return;
    }

    const { name } = parts;
    tex.name = `${partsName}.${name}`;
    console.log(`[saga] load ${partsName} tex:`, tex);

    apply(tex, parts.options);
};

export const fetchAndApply2 = (
    apply: Apply2,
    getParts: GetPartsHandler,
    partsName: string,
    reset: () => void,
) => function* fetchAndApply2Saga(): Generator<any, any, any> {
    const parts: TextureData | null = yield select<GetPartsHandler>(getParts);
    if (parts === null) {
        console.log(`[saga] reset material for ${partsName}`);
        reset();
        return;
    }
    if (!(parts.textureImages.length >= 2)) {
        console.error('[saga] load texture error: not found parts or texImg=', parts);
        reset();
        return;
    }

    const { result: tex1, error: err1 }: TextureRT = yield safe(
        call(fetchTexture, parts.textureImages[0]),
    );
    if (err1 !== null) {
        console.error('[saga] load texture error1:', err1);
        return;
    }
    if (tex1 === null) {
        return;
    }

    const { result: tex2, error: err2 }: TextureRT = yield safe(
        call(fetchTexture, parts.textureImages[1]),
    );
    if (err2 !== null) {
        console.error('[saga] load texture error2:', err2);
        return;
    }
    if (tex2 === null) {
        return;
    }

    const { name } = parts;
    tex1.name = `${partsName}.${name}.front`;
    tex2.name = `${partsName}.${name}.back`;
    console.log(`[saga] load ${partsName} tex:`, tex1, tex2);

    apply(tex1, tex2, parts?.options);
};

export const fetchAndApplyMultiple = (
    apply: ApplyMultiple,
    getParts: GetPartsHandler,
    partsName: string,
    reset: () => void,
) => function* fetchAndApplyMultipleSaga(): Generator<any, any, any> {
    const parts: TextureData | null = yield select<GetPartsHandler>(getParts);
    if (parts === null) {
        console.log(`[saga] reset material for ${partsName}`);
        reset();
        return;
    }

    const { length } = parts.textureImages;
    if (length === 0) {
        console.error(`[saga][${partsName}] texture not found`);
        reset();
        return;
    }

    const tasks = range(0, length - 1).map((index) => call(
        fetchTexture, parts.textureImages[index],
    ));
    const {
        result,
        error,
    }: { result: Array<Texture>; error: Error } = yield safe(all(tasks));
    if (error !== null) {
        console.error('[saga] load texture error:', error);
        return;
    }
    result.forEach((tex, index) => {
        tex.name = `${partsName}.${parts.name}.${index}`;
    });
    console.log(`[saga] load ${partsName} texture:`, result);

    apply(result);
};

export const fetchAndApplyFamilycrestColor = (
    apply: Apply,
    getParts: GetPartsHandler2,
    partsName: string,
    reset: () => void,
) => function* fetchAndApplyFamilycrestColorSaga(): Generator<any, any, any> {
    const parts: FamilycrestColorTextureData | null = yield select<GetPartsHandler2>(getParts);
    if (parts === null) {
        console.log(`[saga] reset material for ${partsName}`);
        reset();
        return;
    }

    const { result: tex, error }: TextureRT = yield safe(call(
        fetchTexture, parts.textureImage,
    ));
    if (error !== null) {
        return;
    }
    if (tex === null) {
        return;
    }

    const { name } = parts;
    tex.name = `${partsName}.${name}`;
    console.log(`[saga] load ${partsName} tex:`, tex);

    apply(tex);
};

export const fetchAndApplyWithMakiwaraFeather = (
    apply: Apply2,
    nonFeather: (mode: MAKIWARA_THREAD_MODE) => void,
    getParts: GetPartsHandler,
    getMode: (state: AppState) => MAKIWARA_THREAD_MODE,
    partsName: string,
    reset: () => void,
) => function* fetchAndApplyWithMakiwaraFeatherSaga(): Generator<any, any, any> {
    const parts: PartsProduct | null = yield select<GetPartsHandler>(getParts);
    if (parts === null) {
        console.log(`[saga] reset material for ${partsName}`);
        reset();
        return;
    }

    const mode: MAKIWARA_THREAD_MODE = yield select(getMode);
    if (isFeatherNone(parts)) {
        reset();
        nonFeather(mode);
        return;
    }

    yield call(fetchAndApply2(apply, getParts, partsName, reset));
};
